/**
 * Wisconsin seller disclosure report (RECR or VLDR) — seller-facing form.
 *
 * The questions come from the backend (a versioned item file keyed to
 * Wis. Stat. § 709.03), never from this component: the statutory item set
 * changes on dates certain, so a client-side copy would drift out of the law.
 *
 * Layout is progressive disclosure by statutory section — 65 answerable items
 * is far too many for one screen, and the section grouping is the statute's own
 * structure. The definitions preamble stays above the questions rather than
 * behind a link: A2 defines "defect", and every B–G question keys off it.
 *
 * Nothing here prefills or infers an answer. Several items (F15, F17) are
 * negatively phrased and invert if mapped from a similar-looking question, so
 * the API marks them `derive_forbidden` and we surface them rather than guess.
 *
 * The owner signs the report in DocuSign, and signs exactly what they
 * reviewed. They fill in the caption (county, and the city, village or town)
 * and the capacity they sign in; the server renders the report as it will
 * print ("Preview the report"); then they agree to the attestation — shown
 * verbatim from the API — and choose "Sign with DocuSign". The sign request
 * quotes the preview's hash and the attestation's version, so a change to the
 * answers or the caption after the preview — or a new signing date — needs a
 * fresh one. The capacity only affects the signed render, so changing it
 * doesn't. The report is dated by DocuSign, the day the owner signs there.
 *
 * While the envelope is out (status `signing`) the answers are locked and the
 * page offers "Continue signing in DocuSign", which reuses the envelope.
 * Reports signed earlier by typed name stay valid and still show who signed.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDisclosureForm } from '../../agents/disclosureAgent';
import { resolveDownloadUrl } from '../../utils/downloadUrl';
import { LEGAL_DISCLAIMER } from '../../utils/disclaimers';
import { copyText } from './paperworkCopy';
import './RecrDisclosureForm.css';

const YES = 'yes';
const NO = 'no';
const NA = 'not_applicable';

const CHOICES = [
  { value: YES, label: 'Yes' },
  { value: NO, label: 'No' },
  { value: NA, label: 'N/A' },
];

/** The statute's municipality types — used if the API omits the list. */
const MUNICIPALITY_TYPES = ['city', 'village', 'town'];

/** Signing for an owner: the signer must name the owner and their own role. */
const REPRESENTATIVE = 'representative';

/** Report statuses (GET /disclosure/form). */
const STATUS_COMPLETED = 'completed';
const STATUS_SIGNING = 'signing';
const SIGNED_WITH_DOCUSIGN = 'docusign';

/** Validation-problem prefixes that belong to the certification block. */
const CERT_PROBLEM_IDS = new Set(['CERT', 'CAPTION', 'CAPACITY']);

const CERT_HINT_ID = 'recr-cert-hint';

/** WI.vldr.voluntary.title_suffix: the leading space is part of the value. */
const VOLUNTARY_TITLE_SUFFIX = ' (Voluntary)';

/**
 * The form's heading: the server's long title ("Wisconsin Real Estate
 * Condition Report", "Wisconsin Vacant Land Disclosure Report"), labeled
 * "(Voluntary)" when the report is voluntary. Never hardcoded to one form.
 */
export function formHeading(form) {
  const base = form?.long_title || form?.title || 'Seller disclosure report';
  return form?.disclosure_basis === 'voluntary' ? base + VOLUNTARY_TITLE_SUFFIX : base;
}

const EMPTY_CERTIFICATION = {
  county: '',
  municipalityType: '',
  // null until the owner edits it: until then the listing's city is shown.
  municipalityName: null,
  capacity: '',
  representedOwner: '',
  representativeCapacity: '',
  // The attestation version the owner agreed to (null: not ticked). A
  // reworded attestation has a new version, so it has to be agreed to again.
  certifiedFor: null,
};

/** The item id a validation problem is about ("B1: …" → "B1"). */
const problemItemId = (problem) => problem.split(':')[0].trim();

/** Item ids mentioned in a validation problem, so rows can be highlighted. */
const problemItemIds = (problems) =>
  new Set(problems.map(problemItemId).filter(Boolean));

const isAnswered = (entry) =>
  entry && entry.value !== null && entry.value !== undefined && entry.value !== '';

/**
 * Locked text (e.g. the lead-in to F10a–c) is read, never answered: it gets no
 * controls and never counts toward progress.
 */
const isAnswerable = (item) =>
  item.is_answerable !== false && item.response_domain !== 'locked_text';

/**
 * Questions the report can't be completed without. Statutory questions are;
 * supplemental detail is optional and never counts toward progress.
 */
const isRequired = (item) =>
  isAnswerable(item) &&
  (typeof item.required === 'boolean'
    ? item.required
    : item.tag !== 'supplemental');

/** The number printed beside an item — its label ("a.") when it has one. */
const itemNumber = (item) => item.label || item.id;

const isBlank = (text) => !(text || '').trim();

/** The trimmed text when it is a non-empty string, else null. */
const notBlankText = (value) =>
  typeof value === 'string' && value.trim() ? value.trim() : null;

const capitalize = (word) =>
  word ? word.charAt(0).toUpperCase() + word.slice(1) : '';

/** ["a", "b", "c"] → "a, b and c" */
const joinWithAnd = (parts) =>
  parts.length < 2
    ? parts.join('')
    : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;

/**
 * "2026-09-27" → "September 27, 2026". It is a calendar date, not an instant,
 * so it is formatted in UTC to keep the browser's time zone from shifting it.
 */
const formatSignatureDate = (isoDate) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate || '');
  if (!match) return '';
  const [year, month, day] = match.slice(1).map(Number);
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
};

/**
 * When DocuSign recorded the signature, as a Wisconsin (Central Time) date.
 * A bare "2026-10-21" is a calendar date and is shown as such.
 */
export const formatSignedAt = (value) => {
  if (!value) return '';
  const dateOnly = formatSignatureDate(value);
  if (dateOnly) return dateOnly;
  const when = new Date(value);
  if (Number.isNaN(when.getTime())) return '';
  return when.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'America/Chicago',
  });
};

/**
 * The answers as the report prints them, in a fixed order: the same answers
 * reloaded from the server fingerprint the same, so a reload alone never
 * voids a preview. A blank explanation counts as none.
 */
const answersFingerprint = (answers) =>
  Object.keys(answers)
    .sort()
    .map((itemId) => [
      itemId,
      answers[itemId]?.value ?? null,
      (answers[itemId]?.explanation || '').trim() || null,
    ]);

/** "City of Madison", or whichever half is known. */
const municipalityPhrase = (type, name) => {
  if (type && name) return `${capitalize(type)} of ${name}`;
  return name || capitalize(type);
};

/** "as trustee for Jane Owner" / "as one of the owners", or ''. */
const capacityPhrase = (details, capacityOptions) => {
  if (
    details.capacity === REPRESENTATIVE &&
    details.representative_capacity &&
    details.represented_owner
  ) {
    return `as ${details.representative_capacity} for ${details.represented_owner}`;
  }
  const option = capacityOptions.find((o) => o.value === details.capacity);
  return option ? `as ${option.label}` : '';
};

/**
 * A single disclosure question, or locked explanatory text. `locked`: the
 * report is out for signature, so its answers are read-only.
 */
const DisclosureItem = ({ item, entry, flagged, locked = false, onAnswer, onExplain }) => {
  const number = itemNumber(item);

  if (!isAnswerable(item)) {
    return (
      <div className="recr-item recr-item--text">
        <div className="recr-item__head">
          <span className="recr-item__id">{number}</span>
          <p className="recr-item__text recr-item__lead">{item.text}</p>
        </div>
      </div>
    );
  }

  // Part of the question's label, so screen readers hear it too.
  const optionalMark = !isRequired(item) && (
    <span className="recr-item__optional"> (optional)</span>
  );
  const value = entry?.value ?? null;
  const explanationRequired =
    item.explanation_required_on?.includes(value) ?? false;
  const explanationMissing =
    explanationRequired && !(entry?.explanation || '').trim();

  if (item.response_domain === 'integer_years') {
    return (
      <div className={`recr-item${flagged ? ' recr-item--flagged' : ''}`}>
        <div className="recr-item__head">
          <span className="recr-item__id">{number}</span>
          <label className="recr-item__text" htmlFor={`recr-${item.id}`}>
            {item.text}
            {optionalMark}
          </label>
        </div>
        <input
          id={`recr-${item.id}`}
          className="recr-item__years"
          type="number"
          min="0"
          step="1"
          disabled={locked}
          value={value ?? ''}
          onChange={(e) =>
            onAnswer(
              item.id,
              e.target.value === '' ? null : parseInt(e.target.value, 10)
            )
          }
        />
        <span className="recr-item__years-unit">years</span>
      </div>
    );
  }

  return (
    <div className={`recr-item${flagged ? ' recr-item--flagged' : ''}`}>
      <div className="recr-item__head">
        <span className="recr-item__id">{number}</span>
        <span className="recr-item__text" id={`recr-label-${item.id}`}>
          {item.text}
          {optionalMark}
        </span>
      </div>

      {item.negative_polarity && (
        <p className="recr-item__polarity">
          Read carefully — answering <strong>Yes</strong> here reports a problem.
        </p>
      )}

      {/* Labelled by the visible question text rather than a hidden <legend>
          duplicate, so screen readers announce it once. */}
      <fieldset
        className="recr-item__choices"
        aria-labelledby={`recr-label-${item.id}`}
      >
        {CHOICES.map((choice) => (
          <label key={choice.value} className="recr-choice">
            <input
              type="radio"
              name={`recr-${item.id}`}
              value={choice.value}
              checked={value === choice.value}
              disabled={locked}
              onChange={() => onAnswer(item.id, choice.value)}
            />
            <span>{choice.label}</span>
          </label>
        ))}
      </fieldset>

      {explanationRequired && (
        <div className="recr-item__explain">
          <label htmlFor={`recr-expl-${item.id}`}>
            Explain why you answered &ldquo;yes&rdquo;{' '}
            <span className="recr-required">required</span>
          </label>
          <textarea
            id={`recr-expl-${item.id}`}
            rows={3}
            readOnly={locked}
            value={entry?.explanation || ''}
            onChange={(e) => onExplain(item.id, e.target.value)}
            placeholder="Describe the condition you are aware of."
          />
          {explanationMissing && (
            <p className="recr-item__explain-warning">
              Wisconsin law requires an explanation for every &ldquo;yes&rdquo;.
              A report without one may be treated as incomplete, which can give a
              buyer who receives it after making an offer a right to rescind (Wis.
              Stat. &sect; 709.05).
            </p>
          )}
        </div>
      )}
    </div>
  );
};

/** One collapsible statutory section. */
const DisclosureSection = ({
  section,
  answers,
  flaggedIds,
  expanded,
  locked,
  onToggle,
  onAnswer,
  onExplain,
}) => {
  const items = [...section.statutory_items, ...section.supplemental_items];
  const required = items.filter(isRequired);
  const answered = required.filter((i) => isAnswered(answers[i.id])).length;
  const complete = answered === required.length && required.length > 0;
  const hasFlag = items.some((i) => flaggedIds.has(i.id));

  return (
    <section
      className={`recr-section${expanded ? ' recr-section--open' : ''}${
        hasFlag ? ' recr-section--flagged' : ''
      }`}
    >
      <h4 className="recr-section__header">
        <button
          type="button"
          className="recr-section__toggle"
          aria-expanded={expanded}
          aria-controls={`recr-section-${section.code}`}
          onClick={() => onToggle(section.code)}
        >
          <span className="recr-section__code">{section.code}</span>
          <span className="recr-section__title">{section.title}</span>
          <span
            className={`recr-section__count${
              complete ? ' recr-section__count--done' : ''
            }`}
          >
            {complete ? '✓ ' : ''}
            {answered}/{required.length}
          </span>
        </button>
      </h4>

      {expanded && (
        <div className="recr-section__body" id={`recr-section-${section.code}`}>
          {section.statutory_items.map((item) => (
            <DisclosureItem
              key={item.id}
              item={item}
              entry={answers[item.id]}
              flagged={flaggedIds.has(item.id)}
              locked={locked}
              onAnswer={onAnswer}
              onExplain={onExplain}
            />
          ))}

          {section.supplemental_items.length > 0 && (
            <>
              <p className="recr-supplemental-note">
                The questions below are <strong>not</strong> part of the report
                prescribed by Wisconsin law. They are optional detail for
                prospective buyers.
              </p>
              {section.supplemental_items.map((item) => (
                <DisclosureItem
                  key={item.id}
                  item={item}
                  entry={answers[item.id]}
                  flagged={flaggedIds.has(item.id)}
                  locked={locked}
                  onAnswer={onAnswer}
                  onExplain={onExplain}
                />
              ))}
            </>
          )}
        </div>
      )}
    </section>
  );
};

/** A required free-text field of the certification block. */
const CertTextField = ({ id, label, value, onChange, hint, wide }) => {
  const hintId = hint ? `${id}-hint` : undefined;
  return (
    <div className={`recr-cert__field${wide ? ' recr-cert__field--wide' : ''}`}>
      <label className="recr-cert__label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className="recr-cert__input"
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-describedby={hintId}
        autoComplete="off"
        required
      />
      {hint && (
        <p id={hintId} className="recr-cert__note">
          {hint}
        </p>
      )}
    </div>
  );
};

/**
 * "Preview the report": the server renders the report exactly as it will
 * print, and it is shown in a fully sandboxed frame (no scripts, no
 * navigation), so the owner reviews precisely what they are about to sign.
 */
const ReportPreview = ({
  preview,
  current,
  busy,
  open,
  canRequest,
  onRequest,
  onClose,
  buttonRef,
}) => {
  let status = '';
  if (!busy && preview) {
    status = current
      ? 'Preview ready. Review the whole report before you sign it.'
      : 'The report has changed since this preview. Preview it again before you sign.';
  }

  return (
    <div className="recr-review">
      <div className="recr-review__bar">
        {/* Not disabled while loading, so keyboard focus stays on it. */}
        <button
          ref={buttonRef}
          type="button"
          className="recr-review__button"
          onClick={onRequest}
          disabled={!canRequest}
          aria-describedby={canRequest ? undefined : CERT_HINT_ID}
        >
          {busy ? 'Preparing preview…' : 'Preview the report'}
        </button>
        <span className="recr-review__status" role="status">
          {status}
        </span>
      </div>

      {open && (busy || preview) && (
        <section
          className={`recr-preview${
            preview && !current ? ' recr-preview--stale' : ''
          }`}
          aria-labelledby="recr-preview-title"
          onKeyDown={(e) => {
            if (e.key === 'Escape') onClose();
          }}
        >
          <div className="recr-preview__head">
            <p id="recr-preview-title" className="recr-preview__title">
              The report as it will print (unsigned)
            </p>
            <button
              type="button"
              className="recr-preview__close"
              onClick={onClose}
            >
              Close
            </button>
          </div>
          {preview && !busy ? (
            <iframe
              className="recr-preview__frame"
              srcDoc={preview.html}
              sandbox=""
              title="Report preview"
            />
          ) : (
            <p className="recr-preview__loading">Preparing the preview…</p>
          )}
        </section>
      )}
    </div>
  );
};

/**
 * The owner's certification, ahead of their DocuSign signature.
 *
 * The heading, the statutory wording and the attestation come from the API
 * and are rendered verbatim — never paraphrased. The owner fills in the
 * caption (county; city, village or town and its name) and the capacity they
 * sign in, reviews the rendered report, then agrees and signs in DocuSign.
 */
const OwnerCertification = ({
  certification,
  attestation,
  counties,
  municipalityTypes,
  capacityOptions,
  values,
  municipalityName,
  certified,
  review,
  missing,
  problems,
  onChange,
}) => {
  const paragraphs = (certification?.text || '')
    .split(/\r?\n/)
    .filter((line) => line.trim());

  return (
    <section
      className={`recr-cert${problems.length > 0 ? ' recr-cert--flagged' : ''}`}
      aria-labelledby="recr-cert-heading"
    >
      <h4 id="recr-cert-heading" className="recr-cert__heading">
        {certification?.heading || 'Owner’s certification'}
      </h4>

      {paragraphs.length > 0 && (
        <div className="recr-cert__statute">
          {paragraphs.map((line, i) => (
            // Statutory text never reorders, so the index is a stable key.
            <p key={i} className="recr-cert__statute-line">
              {line}
            </p>
          ))}
        </div>
      )}

      <div className="recr-cert__caption">
        <div className="recr-cert__field">
          <label className="recr-cert__label" htmlFor="recr-cert-county">
            County
          </label>
          <div className="recr-cert__county">
            <select
              id="recr-cert-county"
              value={values.county}
              onChange={(e) => onChange('county', e.target.value)}
              aria-describedby="recr-cert-county-suffix"
              required
            >
              <option value="">Select county</option>
              {counties.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
            <span id="recr-cert-county-suffix" className="recr-cert__suffix">
              County, Wisconsin
            </span>
          </div>
        </div>

        <fieldset className="recr-cert__field">
          <legend className="recr-cert__label">Municipality type</legend>
          <div className="recr-cert__choices">
            {municipalityTypes.map((type) => (
              <label key={type} className="recr-choice">
                <input
                  type="radio"
                  name="recr-cert-municipality"
                  value={type}
                  checked={values.municipalityType === type}
                  onChange={() => onChange('municipalityType', type)}
                  required
                />
                <span>{capitalize(type)}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <CertTextField
          id="recr-cert-municipality-name"
          label="City, village or town where the property is located"
          value={municipalityName}
          onChange={(v) => onChange('municipalityName', v)}
          hint="Use the municipality shown on your property tax bill. It can differ from your mailing city."
          wide
        />
      </div>

      <fieldset className="recr-cert__field recr-cert__capacity">
        <legend className="recr-cert__label">I am signing as:</legend>
        <div className="recr-cert__options">
          {capacityOptions.map((option) => (
            <label key={option.value} className="recr-choice">
              <input
                type="radio"
                name="recr-cert-capacity"
                value={option.value}
                checked={values.capacity === option.value}
                onChange={() => onChange('capacity', option.value)}
                required
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
        {values.capacity === REPRESENTATIVE && (
          <div className="recr-cert__rep">
            <CertTextField
              id="recr-cert-represented-owner"
              label={"Owner's name"}
              value={values.representedOwner}
              onChange={(v) => onChange('representedOwner', v)}
            />
            <CertTextField
              id="recr-cert-representative-capacity"
              label="Your capacity (for example, trustee)"
              value={values.representativeCapacity}
              onChange={(v) => onChange('representativeCapacity', v)}
            />
          </div>
        )}
      </fieldset>

      <ReportPreview {...review} />

      {attestation?.text && (
        <div className="recr-cert__attest">
          <input
            id="recr-cert-agree"
            type="checkbox"
            checked={certified}
            onChange={(e) =>
              onChange(
                'certifiedFor',
                e.target.checked ? (attestation.version ?? '') : null
              )
            }
            required
          />
          <label htmlFor="recr-cert-agree">{attestation.text}</label>
        </div>
      )}

      <p className="recr-cert__date" data-testid="recr-docusign-note">
        Next, you&rsquo;ll sign the report in DocuSign. It isn&rsquo;t signed
        until you finish there. DocuSign records the date you sign.
      </p>

      {problems.length > 0 && (
        <div className="recr-cert__problems" role="alert">
          <ul>
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>
      )}

      {missing.length > 0 && (
        <p id={CERT_HINT_ID} className="recr-cert__hint">
          To sign the report, {joinWithAnd(missing)}.
        </p>
      )}
    </section>
  );
};

/** What a sign request can't do without (representatives need two more). */
const RESUME_REQUIRED = [
  'county',
  'municipality_type',
  'municipality_name',
  'capacity',
  'attestation_version',
  'preview_sha256',
];

/**
 * The request body that resumes an envelope already out for signature, from
 * the certification the server recorded when signing started — or null when
 * the server didn't send all of it.
 *
 * The server re-validates the whole certification before it reuses the
 * envelope (the preview hash must still match), so a partial body would only
 * be refused. Without a full record the owner confirms the details again on
 * this page instead.
 */
export const resumePayload = (form) => {
  const details = form?.owner_certification_details;
  if (!details) return null;
  const payload = {
    certify: true,
    county: details.county,
    municipality_type: details.municipality_type,
    municipality_name: details.municipality_name,
    capacity: details.capacity,
    attestation_version: details.attestation_version ?? form.attestation?.version,
    preview_sha256: details.preview_sha256 ?? form.preview_sha256,
  };
  if (details.capacity === REPRESENTATIVE) {
    payload.represented_owner = details.represented_owner;
    payload.representative_capacity = details.representative_capacity;
  }
  const required =
    details.capacity === REPRESENTATIVE
      ? [...RESUME_REQUIRED, 'represented_owner', 'representative_capacity']
      : RESUME_REQUIRED;
  return required.every((key) => !isBlank(payload[key])) ? payload : null;
};

/**
 * @param {number} houseId - listing the disclosure belongs to
 * @param {function} [onStatusChange] - called with the form payload whenever
 *   the report's status changes (draft, signing, completed), so the wizard's
 *   publish gate keeps up
 * @param {string} [expectedTitle] - the report the listing's paperwork calls
 *   for, named while the form itself can't be loaded
 * @param {string} [lastSigningError] - the server's `last_signing_error` from
 *   GET /disclosure, when the form payload doesn't carry it
 */
const RecrDisclosureForm = ({ houseId, onStatusChange, expectedTitle, lastSigningError }) => {
  const {
    form,
    answers,
    loading,
    saving,
    error,
    unavailable,
    problems,
    hasUnsaved,
    preview,
    previewing,
    redirecting,
    setAnswer,
    setExplanation,
    save,
    requestPreview,
    sign,
  } = useDisclosureForm(houseId);

  // Tell the wizard when the status moves (e.g. it loads as `completed`
  // after the owner came back from DocuSign).
  const formStatus = form?.status;
  const statusChangeRef = useRef(onStatusChange);
  statusChangeRef.current = onStatusChange;
  useEffect(() => {
    if (formStatus && statusChangeRef.current) statusChangeRef.current(form);
    // Only a change of status is news to the wizard.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formStatus]);

  // One section open at a time — the progressive-disclosure unit.
  const [openSection, setOpenSection] = useState('B');

  // The owner's certification is given at the moment of completion, so it
  // lives here rather than in the saved draft answers.
  const [certification, setCertification] = useState(EMPTY_CERTIFICATION);
  const updateCertification = (field, value) =>
    setCertification((prev) => ({ ...prev, [field]: value }));

  const [previewOpen, setPreviewOpen] = useState(false);
  const previewButtonRef = useRef(null);

  const flaggedIds = useMemo(() => problemItemIds(problems), [problems]);

  const totals = useMemo(() => {
    if (!form) return { answered: 0, total: 0 };
    const items = form.sections
      .flatMap((s) => [...s.statutory_items, ...s.supplemental_items])
      .filter(isRequired);
    return {
      answered: items.filter((i) => isAnswered(answers[i.id])).length,
      total: items.length,
    };
  }, [form, answers]);

  if (loading) {
    return <div className="recr-loading">Loading the disclosure report…</div>;
  }

  // 503: the server doesn't offer this listing's form yet (the VLDR before
  // its edition is published). The server's message says answers are kept.
  if (unavailable && !form) {
    return (
      <div className="recr-unavailable" role="status" data-testid="recr-unavailable">
        <h3>{expectedTitle ? `${expectedTitle}: not available yet` : 'Not available yet'}</h3>
        <p>{unavailable}</p>
      </div>
    );
  }

  if (error && !form) {
    return (
      <div className="recr-error" role="alert">
        Couldn&rsquo;t load the disclosure report: {error}
      </div>
    );
  }

  if (!form) return null;

  const isCompleted = form.status === STATUS_COMPLETED;
  const isSigning = form.status === STATUS_SIGNING;
  const capacityOptions = form.capacity_options || [];
  const requestioned = form.requestioned_items || [];

  // ── Signing ──
  const municipalityName =
    certification.municipalityName ??
    form.caption_defaults?.municipality_name ??
    '';
  const isRepresentative = certification.capacity === REPRESENTATIVE;
  const attestationVersion = form.attestation?.version ?? '';
  const certified =
    certification.certifiedFor !== null &&
    certification.certifiedFor === attestationVersion;

  // The caption is what the preview is rendered from (with the saved
  // answers). The capacity only goes into the signed render, and is never
  // sent with a preview request.
  const captionParams = {
    county: certification.county,
    municipality_type: certification.municipalityType,
    municipality_name: municipalityName.trim(),
  };
  const capacityParams = {
    capacity: certification.capacity,
    ...(isRepresentative
      ? {
          represented_owner: certification.representedOwner.trim(),
          representative_capacity: certification.representativeCapacity.trim(),
        }
      : {}),
  };
  // A preview only stands for the caption and answers it showed, on the date
  // it was rendered for.
  const previewKey = JSON.stringify({
    caption: captionParams,
    answers: answersFingerprint(answers),
  });
  const previewCurrent =
    Boolean(preview) &&
    preview.key === previewKey &&
    (!preview.signature_date ||
      !form.signature_date ||
      preview.signature_date === form.signature_date);

  const captionMissing = [
    !certification.county && 'select the county',
    !certification.municipalityType && 'choose the municipality type',
    isBlank(municipalityName) && 'enter the city, village or town',
  ].filter(Boolean);
  const canPreview = captionMissing.length === 0;
  const signingMissing = [
    ...captionMissing,
    !certification.capacity && 'choose who you are signing as',
    isRepresentative &&
      (isBlank(certification.representedOwner) ||
        isBlank(certification.representativeCapacity)) &&
      'enter the owner’s name and your capacity',
  ].filter(Boolean);

  let attestationMissing = null;
  if (!form.attestation?.text) {
    attestationMissing = 'reload the page to load the signing statement';
  } else if (!certified) {
    attestationMissing = 'tick the certification box';
  }
  const certMissing = [
    ...signingMissing,
    !previewCurrent &&
      (preview ? 'preview the report again' : 'preview the report'),
    attestationMissing,
  ].filter(Boolean);
  const certReady = certMissing.length === 0;
  const certProblems = problems.filter((p) =>
    CERT_PROBLEM_IDS.has(problemItemId(p))
  );
  const busy = saving || redirecting;
  // Why an earlier DocuSign signature didn't count (e.g. the date changed
  // while the owner was signing). Only a draft is waiting to be signed again.
  const signingErrorNotice =
    !isCompleted && !isSigning
      ? notBlankText(form.last_signing_error) || notBlankText(lastSigningError)
      : null;

  // ── Completed ──
  const signed = form.owner_certification_details;
  const signingMethod = form.signing_method || signed?.signing_method || null;
  const signedWithDocuSign = signingMethod === SIGNED_WITH_DOCUSIGN;
  const signedOn = signedWithDocuSign
    ? formatSignedAt(form.signed_at || signed?.signed_at)
    : '';
  const signedCapacity = signed ? capacityPhrase(signed, capacityOptions) : '';
  const signedPlace = signed
    ? [
        signed.county && `${signed.county} County`,
        municipalityPhrase(signed.municipality_type, signed.municipality_name),
      ]
        .filter(Boolean)
        .join('; ')
    : '';

  const handlePreview = async () => {
    if (!canPreview || previewing) return;
    setPreviewOpen(true);
    await requestPreview(captionParams, previewKey);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    previewButtonRef.current?.focus();
  };

  // Signing leaves for DocuSign; the owner comes back to the
  // disclosure-return page, which finishes the report.
  const handleSign = async () => {
    if (!certReady || busy) return;
    await sign({
      certify: true,
      ...captionParams,
      ...capacityParams,
      attestation_version: form.attestation.version,
      preview_sha256: preview.sha256,
    });
  };

  // Out for signature: one click back to DocuSign when the server sent the
  // certification it recorded; otherwise the owner confirms it again below
  // and the server reuses the envelope for the same report.
  const resume = isSigning ? resumePayload(form) : null;
  const handleContinueSigning = async () => {
    if (busy) return;
    if (resume) {
      await sign(resume);
    } else {
      await handleSign();
    }
  };
  const continueDisabled = busy || (!resume && !certReady);

  const certificationBlock = (
    <OwnerCertification
      certification={form.owner_certification}
      attestation={form.attestation}
      counties={form.caption_options?.counties || []}
      municipalityTypes={
        form.caption_options?.municipality_types?.length
          ? form.caption_options.municipality_types
          : MUNICIPALITY_TYPES
      }
      capacityOptions={capacityOptions}
      values={certification}
      municipalityName={municipalityName}
      certified={certified}
      review={{
        preview,
        current: previewCurrent,
        busy: previewing,
        open: previewOpen,
        canRequest: canPreview,
        onRequest: handlePreview,
        onClose: handleClosePreview,
        buttonRef: previewButtonRef,
      }}
      missing={certMissing}
      problems={certProblems}
      onChange={updateCertification}
    />
  );

  return (
    <div className="recr-form">
      <header className="recr-form__intro">
        <h3>{formHeading(form)}</h3>
        {/* The intro is the server's reviewed copy (WI.recr.intro /
            WI.vldr.intro): it explains the 10-days-after-acceptance legal
            deadline and that publishing first is a cutthe6.com rule. */}
        {form.intro && <p data-testid="recr-form-intro">{form.intro}</p>}
      </header>

      {/* Land with outbuildings only: the law doesn't require this report,
          cutthe6.com does. The banner text is the server's reviewed copy
          (WI.vldr.voluntary.intro, served as basis_notice). */}
      {form.disclosure_basis === 'voluntary' && (
        <div className="recr-voluntary" role="note" data-testid="recr-voluntary-banner">
          <strong className="recr-voluntary__title">
            {copyText('WI.vldr.voluntary.banner_title')}
          </strong>
          {form.basis_notice && <p className="recr-voluntary__text">{form.basis_notice}</p>}
        </div>
      )}

      {!form.text_transcribed && (
        <div className="recr-draft-banner" role="status">
          <strong>Draft form — not for execution.</strong> The wording of these
          questions is still being finalised against the official Wisconsin
          form. You can fill this in and save it, but the report cannot be
          issued to buyers yet.
        </div>
      )}

      {requestioned.length > 0 && (
        <div className="recr-requestioned" role="status">
          <strong>The questions now use the official Wisconsin wording.</strong>{' '}
          Please answer them again. {requestioned.length}{' '}
          {requestioned.length === 1 ? 'question needs' : 'questions need'} a
          new answer.
        </div>
      )}

      {/* A2 defines "defect" — every question below depends on it, so it is
          shown inline rather than tucked behind a link. */}
      {form.preamble.length > 0 && (
        <div className="recr-preamble">
          {form.preamble.map((item) => (
            <p key={item.id} className="recr-preamble__item">
              {item.text}
            </p>
          ))}
        </div>
      )}

      <div className="recr-progress" aria-live="polite">
        <div className="recr-progress__bar">
          <span
            className="recr-progress__fill"
            style={{
              width: `${
                totals.total ? (totals.answered / totals.total) * 100 : 0
              }%`,
            }}
          />
        </div>
        <span className="recr-progress__label">
          {totals.answered} of {totals.total} answered
        </span>
      </div>

      {problems.length > 0 && (
        <div className="recr-problems" role="alert">
          <strong>
            {problems.length} item{problems.length === 1 ? '' : 's'} still need
            attention:
          </strong>
          <ul>
            {problems.slice(0, 12).map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
          {problems.length > 12 && (
            <p>…and {problems.length - 12} more.</p>
          )}
        </div>
      )}

      {form.sections.map((section) => (
        <DisclosureSection
          key={section.code}
          section={section}
          answers={answers}
          flaggedIds={flaggedIds}
          expanded={openSection === section.code}
          locked={isSigning}
          onToggle={(code) =>
            setOpenSection((cur) => (cur === code ? null : code))
          }
          onAnswer={setAnswer}
          onExplain={setExplanation}
        />
      ))}

      {isCompleted ? (
        <>
          <div className="recr-completed" role="status">
            ✓{' '}
            {form.title
              ? `${form.title}${form.disclosure_basis === 'voluntary' ? VOLUNTARY_TITLE_SUFFIX : ''}`
              : 'Condition report'}{' '}
            completed and saved to your listing.
            {!form.text_transcribed && (
              <>
                {' '}
                It is <strong>not yet issued to buyers</strong> — see the draft
                notice above. Delivery to prospective buyers is not available
                while the form is in draft.
              </>
            )}
          </div>
          {signedWithDocuSign ? (
            <p className="recr-signed" data-testid="recr-signed">
              Signed with DocuSign{signedOn ? ` on ${signedOn}` : ''}
              {signedCapacity && `, ${signedCapacity}`}
              {signedPlace && ` — ${signedPlace}`}
            </p>
          ) : (
            // Reports signed by typed name before DocuSign stay valid.
            signed?.signature_name && (
              <p className="recr-signed" data-testid="recr-signed">
                Signed electronically by{' '}
                <span className="recr-signed__name">{signed.signature_name}</span>
                {signedCapacity && `, ${signedCapacity}`}
                {signedPlace && ` — ${signedPlace}`}
              </p>
            )
          )}
          {form.document_id != null && (
            <p className="recr-download">
              <a
                className="recr-download__link"
                href={resolveDownloadUrl(
                  `/api/documents/${form.document_id}/download`
                )}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="recr-download"
              >
                Download signed report (PDF)
              </a>
            </p>
          )}
        </>
      ) : isSigning ? (
        <section
          className="recr-signing"
          aria-labelledby="recr-signing-heading"
          data-testid="recr-signing"
        >
          <h4 id="recr-signing-heading" className="recr-signing__heading">
            Waiting for your DocuSign signature
          </h4>
          <p className="recr-signing__text">
            Your report has been sent to DocuSign for your signature. Your
            answers are locked while it is out for signing. The report
            isn&rsquo;t complete, and your listing can&rsquo;t be published,
            until you finish signing in DocuSign.
          </p>
          {!resume && (
            <p className="recr-signing__text" data-testid="recr-signing-confirm">
              To go back to DocuSign, confirm the details below and preview
              the report again. If nothing has changed, including the date,
              you&rsquo;ll continue with the same signing request. Otherwise
              you&rsquo;ll sign a fresh copy.
            </p>
          )}
          {!resume && certificationBlock}
          <div className="recr-actions recr-signing__actions">
            <button
              type="button"
              className="recr-actions__complete"
              onClick={handleContinueSigning}
              disabled={continueDisabled}
              aria-describedby={resume || certReady ? undefined : CERT_HINT_ID}
              data-testid="recr-continue-signing"
            >
              {redirecting
                ? 'Opening DocuSign…'
                : saving
                  ? 'Working…'
                  : 'Continue signing in DocuSign'}
            </button>
          </div>
        </section>
      ) : (
        <>
          {certificationBlock}
          {signingErrorNotice && (
            <div
              className="recr-signing-error"
              role="alert"
              data-testid="recr-signing-error"
            >
              {signingErrorNotice}
            </div>
          )}
          <div className="recr-actions">
            <button
              type="button"
              className="recr-actions__save"
              onClick={save}
              disabled={busy || !hasUnsaved}
            >
              {saving ? 'Saving…' : hasUnsaved ? 'Save progress' : 'Saved'}
            </button>
            <button
              type="button"
              className="recr-actions__complete"
              onClick={handleSign}
              disabled={busy || !certReady}
              aria-describedby={certReady ? undefined : CERT_HINT_ID}
              data-testid="recr-sign-docusign"
            >
              {redirecting
                ? 'Opening DocuSign…'
                : saving
                  ? 'Working…'
                  : 'Sign with DocuSign'}
            </button>
          </div>
        </>
      )}

      {error && form && (
        <p className="recr-error-inline" role="alert">
          {error}
        </p>
      )}

      <p className="recr-disclaimer">
        cutthe6.com is not a licensed real estate broker and does not provide
        legal advice. Real estate licensees may not advise whether an item is a
        defect for the purposes of this report. Consult a licensed Wisconsin
        attorney with questions about your disclosure obligations.
      </p>
      <p className="recr-disclaimer">{LEGAL_DISCLAIMER}</p>
    </div>
  );
};

export default RecrDisclosureForm;
